import React, {useEffect, useContext, useCallback} from 'react';
import {
    Stack,
    Typography,
    Grid,
} from '@mui/material';
import {HookFormTextField} from '../../common/components';
import HookFormDatePicker from '../../common/components/hook-form/HookFormDatePicker';
import { useMutation } from '../../../common/apollo-graphql';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import SSNMask from '../../common/components/mask/SSNMask';
import ClinicDropdownHookFormSelect from '../../clinics/components/ClinicDropdownHookFormSelect';
import AuthContext from '../../auth/context/AuthContext';
import {SAVE_FAX_REFERRAL} from '../gql/FaxQueries';
import ReferringClinicOcrAnnotation, {useOcrClinicForm} from './ReferringClinicOcrAnnotation';
import ProviderDropdownHookFormSelect from '../../clinics/components/ProviderDropdownHookFormSelect';

export const initialValues = {
    firstname: '',
    lastname: '',
    birthdate: '',
    ssn: '',
    diagnosis: '',
    referring_clinic: null,
    receiving_provider: '',
};

// export const getFieldValuesFromReferralObject = (referral) => {
//     if (!referral) {
//         return initialValues;
//     }
//
//     const ssn = referral.ssn?.value;
//
//     return {
//         ...initialValues,
//         ...referral,
//         birthdate: referral.birthdate?.value,
//         ssn: ssn ? StringUtils.formatSSN(ssn) : '',
//         diagnosis: referral.diagnosis?.value,
//         referring_clinic: referral.referring_clinic?.value,
//         receiving_provider: referral.receiving_provider?.value,
//     };
// };

const optionalNumberField = (errorStr) => {
    return yup
        .number(errorStr)
        .truncate()
        .nullable()
        .optional()
        .transform((value, originalValue) => {
            return originalValue === '' || isNaN(value) ? null : value;
        });
};

const getValidationSchema = (canSelectReceivingProvider=true) => {
    const validationSchema = {
        firstname: yup
            .string('Enter patient\'s first name')
            .required('First name is required'),
        lastname: yup
            .string('Enter patient\'s last name')
            .required('Last name is required'),
        birthdate: yup
            .string('Enter patient\'s birth date')
            .required('Birth date is required')
            .typeError('Birth date is required')
            .optional(),
        ssn: yup
            .string('Enter patient\'s SSN')
            .optional()
            .test('ssn-length', 'SSN must be exactly 11 characters', value => {
                // only validate if the field has a value
                if (!value) return true;
                return value.length === 11;
            }),
        diagnosis: yup
            .string('Enter patient\'s diagnosis')
            .required('Diagnosis is required')
            .optional(),
        referring_clinic: optionalNumberField('Select the referring clinic'),
    };

    if (canSelectReceivingProvider) {
        validationSchema['receiving_provider'] = yup.number('Select the provider to receive referral')
            .required('Provider to receive referral is required')
            .typeError('Provider to receive referral is required');
    } else {
        validationSchema['receiving_provider'] = optionalNumberField('Select the provider to receive referral');
    }

    return  yup.object(validationSchema);
};

export function useCreateReferralForm({defaultValues, onSuccess, onFailure, actions}) {
    const [saveFaxReferral, {loading}] = useMutation(SAVE_FAX_REFERRAL, {
        fetchPolicy: 'network-only'
    });

    const validationSchema = getValidationSchema(actions?.can_select_receiving_provider);

    const { control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
        defaultValues: {...initialValues, ...defaultValues},
        resolver: yupResolver(validationSchema),
        reValidateMode: 'onChange',
    });

    const _onSubmit = useCallback((pageRange) => async (dataParams) => {
        try {
            const { data } = await saveFaxReferral({
                variables: {
                    ...dataParams,
                    ...pageRange,
                    ssn: dataParams.ssn ? dataParams.ssn.replace(/-/g, '')  : '',
                    fromReceivedFax: true,
                }
            });

            onSuccess(data);
        } catch (err) {
            console.error("Error submitting referral:", err);
            onFailure(err);
        }
    }, [onSuccess, saveFaxReferral, onFailure]);

    const onSubmit = useCallback((event, pageRange) => {
        handleSubmit(_onSubmit(pageRange))(event);
    }, [_onSubmit, handleSubmit]);

    useEffect(() => {
        for (let i in initialValues) {
            if (defaultValues.hasOwnProperty(i)) {
                setValue(i, defaultValues[i]);
            }
        }

        return () => {
            reset();
        };
    }, [defaultValues, setValue, reset]);

    return {
        control,
        onSubmit,
        setValue,
        errors,
        loading,
    };
}

export default function CreateDetectedReferralForm({control, errors, setValue, actions, ocrDetails=null}) {
    const currentUser = useContext(AuthContext);

    const clinicId = currentUser?.clinic?.id;

    const ocrDetectedReferringClinicName = ocrDetails?.referring_clinic?.value;
    // const ocrDetectedReferringClinicName = 'super family'; // Super Family Residency
    const {onOptionsLoaded: onClinicOptionsLoaded, triggerSelect: triggerSelectClinic, ocrDetails: ocrDetailsForClinic} = useOcrClinicForm({
        setValue,
        referringClinicInputName: ocrDetectedReferringClinicName,
    });

    // const ocrDetectedReferringProviderName = ocrDetails?.receiving_provider?.value;
    // // const ocrDetectedReferringProviderName = 'super family'; // Super Family Residency
    // const {onOptionsLoaded: onProviderOptionsLoaded, triggerSelect: triggerSelectProvider, ocrDetails: ocrDetailsForProvider} = useOcrProviderForm({
    //     setValue,
    //     inputName: ocrDetectedReferringProviderName,
    // });

    return (
        <Stack direction="column"
               spacing={3}>
            {/* Patient Information */}
            <Stack direction="column"
                   justifyContent="flex-start"
                   alignItems="flex-start"
                   spacing={1}
                   sx={{px: 1}}>
                <Typography variant="h6" color="info" gutterBottom>
                    Patient Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <HookFormTextField
                            id="firstname"
                            name="firstname"
                            schema="firstname"
                            placeholder="Enter Patient's First Name"
                            label="First Name"
                            variant="outlined"
                            control={control}
                            errors={errors}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <HookFormTextField
                            id="lastname"
                            name="lastname"
                            schema="lastname"
                            placeholder="Enter Patient's Last Name"
                            label="Last Name"
                            variant="outlined"
                            control={control}
                            errors={errors}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <HookFormDatePicker
                            id="birthdate"
                            name="birthdate"
                            schema="birthdate"
                            placeholder="Enter Patient's birth date"
                            label="Birth Date"
                            variant="outlined"
                            control={control}
                            errors={errors}
                            fullWidth
                            disableFuture
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <HookFormTextField
                            id="ssn"
                            name="ssn"
                            schema="ssn"
                            placeholder="Enter Patient's SSN"
                            label="Social Security Number"
                            variant="outlined"
                            InputProps={{ inputComponent: SSNMask }}
                            control={control}
                            errors={errors}
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </Stack>
            {/* Referral Details */}
            <Stack direction="column"
                   justifyContent="flex-start"
                   alignItems="flex-start"
                   spacing={1}
                   sx={{px: 1}}>
                <Typography variant="h6" color="info" gutterBottom>
                    Referral Details
                </Typography>
                <Grid container
                      spacing={2}>
                    <Grid item xs={12} md={12}>
                        <HookFormTextField
                            id="diagnosis"
                            name="diagnosis"
                            schema="diagnosis"
                            placeholder="Enter Patient's diagnosis"
                            label="Diagnosis"
                            variant="outlined"
                            control={control}
                            errors={errors}
                            fullWidth
                            multiline
                            rows={3}
                        />
                    </Grid>

                    <Grid item xs={12} md={12}>
                        <Stack direction="column"
                               spacing={2}>
                            <ReferringClinicOcrAnnotation
                                triggerSelect={triggerSelectClinic(ocrDetectedReferringClinicName)}
                                ocrDetails={ocrDetailsForClinic}
                            />
                            <ClinicDropdownHookFormSelect
                                schema="referring_clinic"
                                control={control}
                                setValue={setValue}
                                errors={errors}
                                variant="outlined"
                                onOptionsLoaded={onClinicOptionsLoaded}
                                fullWidth
                            />
                        </Stack>
                    </Grid>
                    {
                        actions?.can_select_receiving_provider &&
                        <Grid item xs={12} md={12}>
                            <ProviderDropdownHookFormSelect
                                schema="receiving_provider"
                                placeholder="Enter the provider to receive referral"
                                label="Provider to receive referral"
                                variant="outlined"
                                control={control}
                                errors={errors}
                                clinicId={clinicId}
                                fullWidth
                            />
                        </Grid>
                    }
                </Grid>
            </Stack>
        </Stack>
    );
}